import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";

/**
 * Capture the current animation state of an overlay controller's active spine.
 * Used to create a seamless frame-matched clone.
 *
 * @param {object} overlayController - Controller with { activeSpineKey, prePositionedSpines }
 * @returns {{ animName: string|null, trackTime: number }}
 */
export function captureAnimationState(overlayController) {
  if (!overlayController) return { animName: null, trackTime: 0 };

  const activeSpine = overlayController.activeSpineKey
    ? overlayController.prePositionedSpines.get(overlayController.activeSpineKey)
    : null;

  if (!activeSpine?.state) return { animName: null, trackTime: 0 };

  const currentTrack = activeSpine.state.getCurrent(0);
  if (!currentTrack?.animation) return { animName: null, trackTime: 0 };

  return {
    animName: currentTrack.animation.name,
    trackTime: currentTrack.trackTime,
  };
}

/**
 * Apply a captured animation state to a spine, then optionally queue a
 * follow-up animation (e.g., "collect" after "idle").
 *
 * @param {Spine} spine - Target spine instance
 * @param {string|null} animName - Animation to set (falls back to idle)
 * @param {number} trackTime - Track time to seek to (for frame-matching)
 * @param {object} [options]
 * @param {string|null} [options.followUpAnim] - Animation to queue after (non-looping)
 * @param {number|null} [options.travelDuration] - If set, speeds up followUp to fit this duration (ms)
 */
export function applyAnimationState(spine, animName, trackTime, options = {}) {
  const { followUpAnim = null, travelDuration = null } = options;

  if (!spine?.state) return;

  // Apply the starting animation at the captured frame
  const startAnim = animName || findAnimation(spine, "idle");
  if (startAnim) {
    const entry = spine.state.setAnimation(0, startAnim, true);

    // Frame-match from source
    if (animName && entry) {
      entry.trackTime = trackTime;
    }

    // Apply immediately so first render matches overlay's last frame
    spine.state.update(0);
    spine.state.apply(spine.skeleton);
    spine.skeleton.updateWorldTransform(0);
  }

  // Queue follow-up animation (e.g., collect)
  if (followUpAnim) {
    const followEntry = spine.state.setAnimation(0, followUpAnim, false);

    // Speed up animation to fit within travel duration
    if (followEntry && travelDuration) {
      const animData = spine.skeleton.data.findAnimation(followUpAnim);
      if (animData && animData.duration > 0) {
        const naturalDuration = animData.duration * 1000;
        if (naturalDuration > travelDuration) {
          followEntry.timeScale = naturalDuration / travelDuration;
        }
      }
    }
  }
}

/**
 * Create a fresh Spine instance for traveling animation.
 *
 * @param {string} spineName - Base spine name (e.g., "c" or "j")
 * @param {string} skinName - Skin to apply (e.g., "c5" or "j1")
 * @returns {Spine|null}
 */
export function createTravelingSpine(spineName, skinName) {
  if (!spineName) return null;

  const skeletonAlias = `${spineName}-skeleton`;
  const atlasAlias = `${spineName}-atlas`;
  const assets = PIXI.Assets.cache;

  if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

  try {
    const spine = Spine.from({
      skeleton: skeletonAlias,
      atlas: atlasAlias,
    });

    if (!spine?.skeleton || !spine?.state) return null;

    // Apply skin
    if (skinName && spine.skeleton.data) {
      const skin = spine.skeleton.data.findSkin(skinName);
      if (skin) {
        spine.skeleton.setSkin(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
    }

    return spine;
  } catch {
    return null;
  }
}

/**
 * Find an animation by type keyword (case-insensitive partial match).
 * Tries the requested type first, then falls back to "idle".
 *
 * @param {Spine} spine
 * @param {string} type - Animation type to search for (e.g., "idle", "collect", "win")
 * @returns {string|null} Animation name or null
 */
export function findAnimation(spine, type) {
  if (!spine?.skeleton?.data?.animations) return null;
  const lowerType = type.toLowerCase();

  for (const anim of spine.skeleton.data.animations) {
    if (anim.name.toLowerCase().includes(lowerType)) {
      return anim.name;
    }
  }

  // Fallback: try "idle" if primary type not found
  if (lowerType !== "idle") {
    for (const anim of spine.skeleton.data.animations) {
      if (anim.name.toLowerCase().includes("idle")) {
        return anim.name;
      }
    }
  }

  return null;
}
